import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, useAuth } from "../App";
import { toast } from "sonner";
import { Loader2, CheckCircle2, RefreshCw, AlertCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";

const BillingSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setUser } = useAuth();
  const [status, setStatus] = useState("verifying"); // verifying | success | waiting | error
  const [attempts, setAttempts] = useState(0);
  const [billingStatus, setBillingStatus] = useState(null);

  const checkBillingStatus = useCallback(async () => {
    try {
      const res = await api.get("/billing/status");
      setBillingStatus(res.data);
      
      const subStatus = res.data?.status;
      
      // Check if subscription is active or trialing
      if (subStatus === "active" || subStatus === "trialing") {
        setStatus("success");
        toast.success("Subscription activated! Welcome to ReplyDriven.");
        
        // Refresh user data
        try {
          const userRes = await api.get("/auth/me");
          setUser(userRes.data);
        } catch (e) {
          console.error("Failed to refresh user:", e);
        }
        
        // Redirect to dashboard after a brief delay
        setTimeout(() => navigate('/dashboard', { replace: true }), 2000);
        return true;
      }
      
      return false;
    } catch (error) {
      console.error("Billing status check failed:", error);
      return false;
    }
  }, [navigate, setUser]);

  useEffect(() => {
    let pollInterval;
    let timeoutId;
    let forceRedirectId;
    
    const startPolling = async () => {
      // Initial check
      const success = await checkBillingStatus();
      if (success) return;
      
      // Start polling every 2 seconds
      pollInterval = setInterval(async () => {
        setAttempts(prev => prev + 1);
        const success = await checkBillingStatus();
        if (success) {
          clearInterval(pollInterval);
        }
      }, 2000);
      
      // P0: After 10 seconds, stop polling and show manual options
      timeoutId = setTimeout(() => {
        clearInterval(pollInterval);
        setStatus("waiting");
      }, 10000);
      
      // P0: Force redirect fallback after 30 seconds to prevent blank screen
      // Even if webhook hasn't arrived, redirect to dashboard which will show appropriate state
      forceRedirectId = setTimeout(() => {
        clearInterval(pollInterval);
        console.log("Force redirect triggered after timeout");
        navigate('/dashboard', { replace: true });
      }, 30000);
    };
    
    startPolling();
    
    return () => {
      if (pollInterval) clearInterval(pollInterval);
      if (timeoutId) clearTimeout(timeoutId);
      if (forceRedirectId) clearTimeout(forceRedirectId);
    };
  }, [checkBillingStatus, navigate]);

  const handleManualRefresh = async () => {
    setStatus("verifying");
    setAttempts(0);
    const success = await checkBillingStatus();
    if (!success) {
      setStatus("waiting");
    }
  };

  const handleGoToDashboard = () => {
    navigate('/dashboard', { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6">
      <div className="text-center max-w-md">
        {status === "verifying" && (
          <>
            <Loader2 className="w-16 h-16 text-[#D4F34A] animate-spin mx-auto mb-6" />
            <h1 className="font-heading text-2xl font-bold text-white mb-2">
              Confirming your subscription...
            </h1>
            <p className="text-text-muted mb-4">
              Please wait while we verify your payment with Lemon Squeezy.
            </p>
            <p className="text-xs text-text-muted">
              Check #{attempts + 1}
            </p>
          </>
        )}
        
        {status === "success" && (
          <>
            <div className="w-20 h-20 rounded-full bg-[#D4F34A]/20 flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-10 h-10 text-[#D4F34A]" />
            </div>
            <h1 className="font-heading text-2xl font-bold text-white mb-2">
              You&apos;re all set! ✅
            </h1>
            <p className="text-text-muted mb-4">
              {billingStatus?.status === "trialing" 
                ? `Your ${billingStatus?.trial_days_remaining || 7}-day free trial has started.`
                : `Welcome to ReplyDriven! Your subscription is now active.`
              }
            </p>
            
            {/* Clear next steps */}
            <div className="bg-surface border border-[#262626] rounded-lg p-4 mb-4 text-left">
              <h3 className="font-semibold text-white mb-2">What&apos;s next?</h3>
              <ul className="text-sm text-text-muted space-y-2">
                <li className="flex items-start gap-2">
                  <span className="text-[#D4F34A]">1.</span>
                  <span>Connect your Shopify store to start analyzing</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#D4F34A]">2.</span>
                  <span>We&apos;ll run your first analysis immediately</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#D4F34A]">3.</span>
                  <span>Every Monday, you&apos;ll receive your biggest profit leak via email</span>
                </li>
              </ul>
            </div>
            
            <Button
              onClick={handleGoToDashboard}
              className="w-full bg-[#D4F34A] text-black hover:bg-[#D4F34A]/90 font-bold py-6 mb-2"
            >
              Go to Dashboard
            </Button>
            
            <p className="text-sm text-text-muted">
              Redirecting automatically...
            </p>
          </>
        )}
        
        {status === "waiting" && (
          <>
            <div className="w-20 h-20 rounded-full bg-[#FFA500]/10 flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="w-10 h-10 text-[#FFA500]" />
            </div>
            <h1 className="font-heading text-2xl font-bold text-white mb-2">
              We&apos;re finishing setup...
            </h1>
            <p className="text-text-muted mb-6">
              Your payment was received! It may take a moment for our system to update. 
              This usually happens within a few seconds.
            </p>
            
            <Card className="bg-surface border-[#262626] mb-6">
              <CardContent className="pt-6 space-y-4">
                <Button
                  onClick={handleManualRefresh}
                  className="w-full bg-[#D4F34A] text-black hover:bg-[#D4F34A]/90 font-bold py-6"
                >
                  <RefreshCw className="w-4 h-4 mr-2" /> Refresh
                </Button>
                <Button
                  onClick={handleGoToDashboard}
                  variant="outline"
                  className="w-full border-[#262626] hover:bg-[#1E1E1E] py-6"
                >
                  Go to Dashboard
                </Button>
              </CardContent>
            </Card>
            
            <p className="text-xs text-text-muted">
              If your subscription does not appear after a few minutes, please contact support at hello@replydriven.com
            </p>
          </>
        )}
        
        {status === "error" && (
          <>
            <div className="w-20 h-20 rounded-full bg-[#FF453A]/10 flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="w-10 h-10 text-[#FF453A]" />
            </div>
            <h1 className="font-heading text-2xl font-bold text-white mb-2">
              Something went wrong
            </h1>
            <p className="text-text-muted mb-6">
              We could not verify your subscription. Please try again or contact support.
            </p>
            <Button
              onClick={handleGoToDashboard}
              className="bg-[#D4F34A] text-black hover:bg-[#D4F34A]/90 font-bold"
            >
              Go to Dashboard
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default BillingSuccess;
